import React, { memo, useCallback } from 'react'
import { BdaServiceApplicationProperty, BdaServiceProperty, BdaServiceResourceProperty } from '../../models/BdaService'
import InputTokenizer from '../generics/InputTokenizer';
import { Flex, Input, Slider, Tooltip } from 'antd';
import { setObjectType } from '../../utils';
import ScriptEditor from '../common/ScriptEditor';
import { Badge, Button, Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import IconButton from '../common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import classNames from 'classnames';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import PresetValuesSelect from './PresetValuesSelect';

type Props = {
  prop: BdaServiceProperty;
  className?: string;
  onPropChange: (key: string, checkbox?: boolean, pure?: boolean) => (evt: any) => void;
  onBlur?: VoidFunction;
  onOpenModal?: VoidFunction;
  onToggleInterpolation?: VoidFunction;
  updateServiceProperties?: VoidFunction;
  wideMode?: boolean;
}

const sliderFormatter = (prop: BdaServiceResourceProperty) => (value?: number) => `${value}${prop.metricUnit}`;

function ServicePropertyFormGroup({ className, prop, onPropChange, onBlur, onOpenModal, onToggleInterpolation, updateServiceProperties, wideMode }: Props) {
  const environmentVariables = useSelector((state: RootState) => state.designer.environmentVariables);
  const envVarLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  const renderInterpolationButton = useCallback(() => {
    return wideMode
      ? (
        <Button variant={prop.interpolationEnabled ? "primary" : "light"} className="border" size="sm" onClick={onToggleInterpolation}>
          <FontAwesomeIcon icon={solid('code')} />
          <span className="ms-2">Toggle Interpolation</span>
        </Button>
      )
      : (
        <IconButton
          tooltip={{
            title: (
              <>
                <b className="d-block">Toggle Variable Interpolation</b>
                <span>{envVarLabels.plural} can be interpolated with <Badge bg="dark" className="p-1">{"{{ variableName }}"}</Badge></span>
              </>
            ),
            placement: "left",
            mouseEnterDelay: 0.5,
          }}
          className={classNames("interpolation-button ms-3", { enabled: prop.interpolationEnabled })}
          onClick={onToggleInterpolation}
        >
          <FontAwesomeIcon icon={solid('code')} />
        </IconButton>
      )
  }, [prop, wideMode, envVarLabels, onToggleInterpolation]);

  const renderFormGroupInput = useCallback(() => {
    if (prop.presetValues?.length) return (
      <PresetValuesSelect
        prop={prop}
        onPropChange={onPropChange}
        onBlur={onBlur}
        wideMode={wideMode}
      />
    )

    if (prop.interpolationEnabled) return (
      <InputTokenizer
        placeholder={prop.defaultValue || `${prop.key} value`}
        value={prop.value || ""}
        onChange={onPropChange(prop.key!, false, true)}
        onBlur={onBlur}
        variables={environmentVariables}
      />
    )

    if (prop.key === window.__env.DESIGNER__AGENT_PROMPT_PROPERTY) return (
      <Input.TextArea
        value={prop.value || ""}
        placeholder={prop.defaultValue || `${prop.key} value`}
        onChange={onPropChange(prop.key!)}
        onBlur={onBlur}
      />
    )

    if (prop.type === 'resource' && setObjectType<BdaServiceResourceProperty>(prop)) return (
      <div className="px-2">
        <Slider
          step={prop.step}
          value={prop.value}
          defaultValue={prop.defaultValue}
          min={prop.allowedRange?.min}
          max={prop.allowedRange?.max}
          marks={{
            [prop.allowedRange?.min || '0']: <span>{prop.allowedRange?.min || '0'}{prop.metricUnit}</span>,
            [prop.allowedRange?.max || '100']: <span>{prop.allowedRange?.max || '100'}{prop.metricUnit}</span>
          }}
          tooltip={{ formatter: sliderFormatter(prop) }}
          onChange={onPropChange(prop.key!, false, true)}
          onChangeComplete={updateServiceProperties}
        />
      </div>
    )

    return prop.valueType === "JSON"
      ? (
        <ScriptEditor
          height={wideMode ? 450 : 150}
          value={prop.value || prop.defaultValue || ""}
          onChange={onPropChange(prop.key!, false, true)}
          onBlur={updateServiceProperties}
        />
      )
      : (prop as BdaServiceApplicationProperty).obfuscated
        ? (
          <Input.Password
            value={prop.value || ""}
            placeholder={prop.defaultValue || `${prop.key} value`}
            onChange={onPropChange(prop.key!)}
            onBlur={onBlur}
          />
        )
        : (
          <Form.Control
            size="sm"
            type={prop.valueType === 'INT' ? 'number' : 'text'}
            placeholder={prop.defaultValue || `${prop.key} value`}
            value={prop.value || ""}
            onChange={onPropChange(prop.key!)}
            onBlur={onBlur}
          />
        )
  }, [prop, environmentVariables, onBlur, onPropChange, updateServiceProperties, wideMode]);

  const renderFormGroupContent = useCallback(() => {
    if (prop.valueType === "BOOLEAN") {
      const value = typeof prop.value === "boolean" ? prop.value : prop.value === "true";
      const defaultChecked = typeof prop.defaultValue === "boolean" ? prop.defaultValue : prop.defaultValue === "true";
      const checked = typeof prop.value === 'boolean' ? value : defaultChecked;

      return (
        <>
          <Form.Check
            type="checkbox"
            label={prop.key}
            id={`${prop.key}-checkbox`}
            checked={checked}
            onChange={onPropChange(prop.key!, true)}
            className="mb-0"
          />
          <Form.Text>{prop.description}</Form.Text>
        </>
      )
    }

    return (
      <>
        <Flex align="center">
          <Col className="overflow-hidden">
            <Tooltip mouseEnterDelay={0.5} title={`${prop.key} ${prop.mandatory ? '*' : ''}`}>
              <Form.Label className="text-truncate">{prop.key} {prop.mandatory && '*'}</Form.Label>
            </Tooltip>
          </Col>
          {prop.type !== 'resource' && !wideMode && (
            <IconButton tooltip={{ title: "Expand Input", placement: 'left', mouseEnterDelay: 0.5 }} className="p-0" onClick={onOpenModal}>
              <FontAwesomeIcon icon={solid('expand')} />
            </IconButton>
          )}
          {!prop.presetValues?.length && renderInterpolationButton()}
        </Flex>
        {renderFormGroupInput()}
        <Form.Text>{prop.description}</Form.Text>
      </>
    )
  }, [prop, onOpenModal, onPropChange, renderFormGroupInput, wideMode, renderInterpolationButton]);

  return (
    <Form.Group controlId={prop.valueType !== 'BOOLEAN' ? `${prop.key}-form-control` : undefined} className={className}>
      {renderFormGroupContent()}
    </Form.Group>
  )
}

export default memo(ServicePropertyFormGroup);